import React, { useMemo, useState } from "react";
import { Search, RefreshCw, ExternalLink, Check, PlusCircle, Loader2 } from "lucide-react";
import { dealsApi, toolsApi } from "../api";
import { toast } from "sonner";

const amortize = (loan, ratePct, years = 30) => {
  const mr = ratePct / 100 / 12;
  const n = years * 12;
  if (mr <= 0) return loan / n;
  return (loan * mr * Math.pow(1 + mr, n)) / (Math.pow(1 + mr, n) - 1);
};

const money = (n) => "$" + Math.round(n).toLocaleString();
const emptyForm = {
  address: "", price: "", propType: "Single Family", rentalType: "Long-Term Rental", beds: "3", baths: "2", sqft: "", market: "Florida",
  rent: "", downPct: "20", rate: "7.5", taxMo: "0", insMo: "0", hoaMo: "0",
  badge: "New Listing", publish: true, notes: "",
};

const SOURCES = [
  ["ZILLOW", "https://www.zillow.com"],
  ["REDFIN", "https://www.redfin.com"],
  ["REALTOR.COM", "https://www.realtor.com"],
  ["FB MARKETPLACE", "https://www.facebook.com/marketplace"],
];

const FIND_LINKS = [
  ["Zillow Investment Filter", "https://www.zillow.com"],
  ["Redfin Multi-Family", "https://www.redfin.com"],
  ["Facebook Marketplace", "https://www.facebook.com/marketplace"],
  ["LoopNet Multi-Family", "https://www.loopnet.com"],
  ["CREXi Residential", "https://www.crexi.com"],
];

const Label = ({ children, hint }) => (
  <label style={{ display: "block", fontSize: "0.64rem", letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--muted)", marginBottom: "0.5rem" }}>
    {children} {hint && <span style={{ color: "var(--gold)" }}>{hint}</span>}
  </label>
);

export default function AddPropertyView({ onSaved, goTo }) {
  const [f, setF] = useState(emptyForm);
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const num = (v) => Number(v) || 0;

  const estimateExpenses = (price) => {
    set("taxMo", Math.round((price * 0.012) / 12));
    set("insMo", Math.round((price * 0.0065) / 12));
  };
  const estimateRent = (price, type) => {
    const mult = type === "Duplex" ? 0.011 : type === "Triplex" || type === "Quadplex" || type === "Multi-Family" ? 0.011 : 0.0085;
    return Math.round(price * mult);
  };

  const PTYPE_MAP = { "Single Family": "Single Family", "Condo": "Condo", "Townhouse": "Townhouse", "Multi-Family": "Multi-Family", "Apartment": "Multi-Family", "Duplex": "Duplex" };

  const autoFill = () => {
    const price = num(f.price);
    if (!price) { toast.error("Enter a list price first, then Auto-Fill estimates rent & expenses"); return; }
    setLoading(true);
    try {
      set("rent", String(estimateRent(price, f.propType)));
      estimateExpenses(price);
      toast.success("Rent & expenses estimated from price");
    } finally {
      setLoading(false);
    }
  };

  const reEstimate = () => {
    const price = num(f.price);
    if (!price) { toast.error("Enter a list price first"); return; }
    estimateExpenses(price);
    toast("Expenses re-estimated from price");
  };

  const calc = useMemo(() => {
    const price = num(f.price);
    const rent = num(f.rent);
    const loan = price * (1 - num(f.downPct) / 100);
    const pi = price ? amortize(loan, num(f.rate)) : 0;
    const taxIns = num(f.taxMo) + num(f.insMo);
    const pitia = pi + taxIns + num(f.hoaMo);
    const dscr = pitia > 0 ? rent / pitia : 0;
    const cf = rent - pitia;
    return { loan, pi, taxIns, pitia, rent, dscr, cf, ready: price > 0 && rent > 0 };
  }, [f]);

  const save = async (publish) => {
    if (!f.address || !f.price) { toast.error("Address and list price are required"); return; }
    const [street, ...rest] = f.address.split(",");
    const city = rest.join(",").trim();
    try {
      await dealsApi.create({
        address: street.trim(), city, market: f.market, type: `${f.propType} · ${f.rentalType}`, rentalType: f.rentalType,
        badge: f.badge, price: num(f.price), rent: num(f.rent), payment: Math.round(calc.pitia) || null,
        downPct: num(f.downPct), beds: String(f.beds), baths: String(f.baths), sqft: num(f.sqft), status: publish ? "LIVE" : "DRAFT",
      });
      toast.success(publish ? "Published live" : "Saved as draft");
      onSaved && onSaved();
      setF(emptyForm);
      goTo && goTo(publish ? "Dashboard" : "All Properties");
    } catch (e) { toast.error("Could not save property"); }
  };

  const previewRows = [
    ["Loan Amount", calc.ready ? money(calc.loan) : "—"],
    ["Monthly P&I", calc.ready ? money(calc.pi) : "—"],
    ["Tax + Insurance", calc.taxIns ? money(calc.taxIns) : "—"],
    ["Total PITIA", calc.ready ? money(calc.pitia) : "—"],
    ["Market Rent", num(f.rent) ? money(num(f.rent)) : "—"],
  ];

  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 320px", gap: "1.4rem", alignItems: "start" }}>
      <div style={{ display: "grid", gap: "1.4rem" }}>
        {/* Banner */}
        <div className="card" style={{ padding: "1.4rem 1.6rem", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "1rem", background: "linear-gradient(180deg, rgba(201,162,75,0.06), transparent)" }}>
          <div style={{ display: "flex", gap: "0.9rem", alignItems: "flex-start" }}>
            <PlusCircle size={20} color="var(--gold)" style={{ marginTop: 2 }} />
            <div>
              <div style={{ fontWeight: 500, color: "var(--text)" }}>2-Minute Deal Upload</div>
              <div style={{ fontSize: "0.8rem", color: "var(--muted)" }}>Find a deal on Zillow, Redfin, or MLS → paste the address below → everything auto-calculates → one click to publish live on your site.</div>
            </div>
          </div>
          <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
            {SOURCES.map(([label, url]) => (
              <a key={label} href={url} target="_blank" rel="noreferrer" style={{ fontSize: "0.62rem", letterSpacing: "0.08em", padding: "0.45rem 0.7rem", borderRadius: 3, border: "1px solid rgba(255,255,255,0.14)", color: "var(--text)", textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 4 }}>{label} <ExternalLink size={11} /></a>
            ))}
          </div>
        </div>

        {/* Step 1 */}
        <div className="card" style={{ padding: "1.8rem" }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "1.1rem", flexWrap: "wrap", gap: 8 }}>
            <span className="eyebrow" style={{ color: "var(--muted)" }}>Step 1 — Paste Address</span>
            <span style={{ fontSize: "0.66rem", color: "var(--muted-2)", letterSpacing: "0.08em" }}>ENTER FULL ADDRESS OR COPY-PASTE FROM ZILLOW</span>
          </div>
          <div style={{ display: "flex", gap: "0.8rem" }}>
            <div style={{ flex: 1, position: "relative" }}>
              <Search size={16} style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", color: "var(--muted-2)" }} />
              <input className="field" style={{ paddingLeft: 40 }} placeholder="4823 Palmetto Ridge Dr, Tampa, FL 33610" value={f.address} onChange={(e) => set("address", e.target.value)} />
            </div>
            <button className="btn-gold" onClick={autoFill} disabled={loading}>{loading ? <Loader2 size={13} className="spin" /> : <RefreshCw size={13} />} {loading ? "Estimating…" : "Auto-Estimate"}</button>
          </div>
        </div>

        {/* Step 2 */}
        <div className="card" style={{ padding: "1.8rem" }}>
          <div className="eyebrow" style={{ color: "var(--muted)", marginBottom: "1.3rem" }}>Step 2 — Enter the Numbers from the Listing</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1.1rem", marginBottom: "1.1rem" }}>
            <div><Label hint="FROM LISTING">List Price ($)</Label><input className="field" type="number" value={f.price} onChange={(e) => set("price", e.target.value)} /></div>
            <div><Label>Property Type</Label>
              <select className="field" value={f.propType} onChange={(e) => set("propType", e.target.value)}>
                {["Single Family", "Duplex", "Triplex", "Quadplex", "Condo", "Townhouse", "Multi-Family"].map((p) => <option key={p}>{p}</option>)}
              </select>
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1.1rem", marginBottom: "1.1rem" }}>
            <div><Label>Rental Strategy</Label>
              <select className="field" data-testid="add-rental-type" value={f.rentalType} onChange={(e) => set("rentalType", e.target.value)}>
                {["Long-Term Rental", "Short-Term Rental"].map((p) => <option key={p}>{p}</option>)}
              </select>
            </div>
            <div />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: "1.1rem", marginBottom: "1.3rem" }}>
            <div><Label>Beds</Label><input className="field" value={f.beds} onChange={(e) => set("beds", e.target.value)} /></div>
            <div><Label>Baths</Label><input className="field" value={f.baths} onChange={(e) => set("baths", e.target.value)} /></div>
            <div><Label>Sq Ft</Label><input className="field" type="number" value={f.sqft} onChange={(e) => set("sqft", e.target.value)} /></div>
            <div><Label>Market</Label>
              <select className="field" value={f.market} onChange={(e) => set("market", e.target.value)}>
                {["Florida", "Texas", "Georgia", "Carolinas", "Midwest", "Maryland", "Virginia", "DC / DMV", "Alabama", "Missouri", "Kansas", "Illinois", "Tennessee", "Michigan", "New York", "Pennsylvania"].map((m) => <option key={m}>{m}</option>)}
              </select>
            </div>
          </div>

          <div style={{ border: "1px solid var(--gold)", borderRadius: 4, padding: "1.3rem", background: "var(--gold-soft)", display: "grid", gridTemplateColumns: "1.2fr 1fr", gap: "1.2rem", alignItems: "center", marginBottom: "1.3rem" }}>
            <div><Label>{f.rentalType === "Short-Term Rental" ? "Est. Monthly STR Revenue ($) — enter manually or use Auto-Estimate" : "Monthly Rent ($) — enter manually or use Auto-Estimate"}</Label><input className="field" type="number" value={f.rent} onChange={(e) => set("rent", e.target.value)} /></div>
            <div style={{ fontSize: "0.72rem", color: "var(--muted)", lineHeight: 1.6 }}>
              <div style={{ color: "var(--gold)", marginBottom: 4 }}>Quick estimate from list price:</div>
              Single family: price × 0.85%<br />Duplex: price × 1.1%<br />e.g. $285K × 0.85% = $2,420/mo
            </div>
          </div>

          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "1rem" }}>
            <span style={{ fontSize: "0.64rem", letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--muted-2)" }}>Expenses (auto-estimated from price — override if you have exact figures)</span>
            <button onClick={reEstimate} style={{ background: "none", border: "none", color: "var(--gold)", cursor: "pointer", fontSize: "0.7rem", display: "inline-flex", alignItems: "center", gap: 4 }}><RefreshCw size={12} /> Re-estimate</button>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: "1.1rem", marginBottom: "1rem" }}>
            <div><Label>Down Pymt %</Label><input className="field" value={f.downPct} onChange={(e) => set("downPct", e.target.value)} /></div>
            <div><Label>Rate %</Label><input className="field" value={f.rate} onChange={(e) => set("rate", e.target.value)} /></div>
            <div><Label>Tax ($/mo)</Label><input className="field" value={f.taxMo} onChange={(e) => set("taxMo", e.target.value)} /></div>
            <div><Label>Ins ($/mo)</Label><input className="field" value={f.insMo} onChange={(e) => set("insMo", e.target.value)} /></div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1.1rem", alignItems: "center" }}>
            <div><Label>HOA ($/mo) — enter 0 if none</Label><input className="field" value={f.hoaMo} onChange={(e) => set("hoaMo", e.target.value)} /></div>
            <div style={{ fontSize: "0.72rem", color: "var(--muted-2)", lineHeight: 1.5 }}>Tax auto-estimated at 1.2% annually, insurance at 0.65% annually.</div>
          </div>
        </div>

        {/* Step 3 */}
        <div className="card" style={{ padding: "1.8rem" }}>
          <div className="eyebrow" style={{ color: "var(--muted)", marginBottom: "1.3rem" }}>Step 3 — Publish</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1.1rem", marginBottom: "1.1rem" }}>
            <div><Label>Badge</Label>
              <select className="field" value={f.badge} onChange={(e) => set("badge", e.target.value)}>
                {["New Listing", "🔥 Hot Deal", "🔥 Best DSCR", "STR Eligible", "No-Ratio OK", "Price Drop"].map((b) => <option key={b}>{b}</option>)}
              </select>
            </div>
            <div><Label>Status</Label>
              <button onClick={() => set("publish", !f.publish)} style={{ width: "100%", display: "flex", alignItems: "center", gap: 10, background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 2, padding: "0.85rem 1rem", cursor: "pointer", color: "var(--text)", fontSize: "0.9rem" }}>
                <span style={{ width: 20, height: 20, borderRadius: 3, background: f.publish ? "var(--green)" : "transparent", border: `1px solid ${f.publish ? "var(--green)" : "rgba(255,255,255,0.3)"}`, display: "flex", alignItems: "center", justifyContent: "center" }}>{f.publish && <Check size={14} color="#0a0a0c" />}</span>
                {f.publish ? "Live" : "Draft"}
              </button>
            </div>
          </div>
          <div style={{ marginBottom: "1.3rem" }}>
            <Label>Deal Source / Notes (internal only — not shown on site)</Label>
            <textarea className="field" rows={3} placeholder="e.g. Found on Zillow 5/14. Called listing agent. MLS#12345. Off-market seller contact: John Smith 555-0100..." value={f.notes} onChange={(e) => set("notes", e.target.value)} />
          </div>
          <div style={{ display: "flex", gap: "0.7rem", flexWrap: "wrap" }}>
            <button className="btn-gold" onClick={() => save(true)}><Check size={14} /> Publish Live</button>
            <button className="btn-dark" onClick={() => save(false)}>Save Draft</button>
            <button className="btn-dark" onClick={() => setF(emptyForm)}>Clear</button>
          </div>
        </div>
      </div>

      {/* Right preview sidebar */}
      <div className="card" style={{ padding: "1.6rem", position: "sticky", top: "1.5rem" }}>
        <div className="eyebrow" style={{ color: "var(--muted)", marginBottom: "1rem" }}>Live DSCR Preview</div>
        <div style={{ textAlign: "center", padding: "0.7rem", border: "1px solid rgba(201,162,75,0.3)", borderRadius: 3, marginBottom: "1.3rem", background: "var(--gold-soft)" }}>
          <span style={{ color: "var(--gold)", fontSize: "0.72rem", letterSpacing: "0.08em" }}>{calc.ready ? "DSCR CALCULATED BELOW" : "ENTER PRICE & RENT TO CALCULATE"}</span>
        </div>
        <div style={{ display: "grid", gap: "0.7rem", marginBottom: "1.2rem" }}>
          {previewRows.map(([l, v], i) => (
            <div key={i} style={{ display: "flex", justifyContent: "space-between", paddingBottom: "0.55rem", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
              <span style={{ color: "var(--muted)", fontSize: "0.8rem" }}>{l}</span>
              <span className="font-serif" style={{ fontSize: "1rem", color: "var(--text)" }}>{v}</span>
            </div>
          ))}
        </div>
        <div style={{ textAlign: "center", marginBottom: "1.2rem" }}>
          <div className="eyebrow" style={{ color: "var(--muted-2)" }}>DSCR Ratio</div>
          <div className="font-serif" style={{ fontSize: "2.6rem", color: !calc.ready ? "var(--muted-2)" : calc.dscr >= 1 ? "var(--green)" : "var(--red)" }}>{calc.ready ? calc.dscr.toFixed(2) : "—"}</div>
          <div className="eyebrow" style={{ color: "var(--muted-2)", marginTop: "0.6rem" }}>Monthly CF</div>
          <div className="font-serif" style={{ fontSize: "1.4rem", color: !calc.ready ? "var(--muted-2)" : calc.cf >= 0 ? "var(--green)" : "var(--red)" }}>{calc.ready ? (calc.cf >= 0 ? "+" : "-") + money(Math.abs(calc.cf)) : "—"}</div>
        </div>
        {!calc.ready && <p style={{ fontSize: "0.75rem", color: "var(--muted-2)", textAlign: "center", marginBottom: "1.2rem" }}>Enter price & rate to see financing options</p>}

        <div style={{ borderTop: "1px solid rgba(255,255,255,0.06)", paddingTop: "1rem" }}>
          <div className="eyebrow" style={{ color: "var(--muted-2)", fontSize: "0.6rem", marginBottom: "0.6rem" }}>Quick Rent Estimator</div>
          <div style={{ fontSize: "0.75rem", color: "var(--muted)", lineHeight: 1.8 }}>
            If no HC data yet, use:<br />
            <span style={{ color: "var(--gold)" }}>SFR</span> → price × 0.85%<br />
            <span style={{ color: "var(--gold)" }}>Duplex</span> → price × 1.1%<br />
            <span style={{ color: "var(--gold)" }}>STR</span> → price × 1.3–1.6%
          </div>
        </div>
        <div style={{ borderTop: "1px solid rgba(255,255,255,0.06)", paddingTop: "1rem", marginTop: "1rem" }}>
          <div className="eyebrow" style={{ color: "var(--muted-2)", fontSize: "0.6rem", marginBottom: "0.6rem" }}>Find Deals Free</div>
          <div style={{ display: "grid", gap: "0.4rem" }}>
            {FIND_LINKS.map(([label, url]) => (
              <a key={label} href={url} target="_blank" rel="noreferrer" className="link-hover" style={{ fontSize: "0.78rem", color: "var(--muted)", textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 4 }}>{label} <ExternalLink size={11} /></a>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
